import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../redux/loader/loaderActions';
import { url } from '../../api';
import {
    Container, Grid, Paper, Title, Text, Button, Group, Stack, Badge,
    Image, createStyles, TextInput, Loader, ActionIcon, Transition
} from '@mantine/core';

import { IconSearch, IconShoppingCart, IconArrowRight } from '@tabler/icons-react';
import { addCartItem } from '../../redux/cart/cartActions';
import { toast } from 'react-toastify';

const useStyles = createStyles((theme) => ({
    wrapper: {
        paddingTop: 120,
        paddingBottom: 80,
        minHeight: '100vh',
        backgroundColor: '#050505',
        backgroundImage: 'radial-gradient(circle at center, #1a1a1a 0%, #000000 100%)',
        backgroundAttachment: 'fixed',
        [theme.fn.smallerThan('sm')]: {
            paddingTop: 80,
            paddingBottom: 60,
        },
    },
    glassPanel: {
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: theme.radius.lg,
        overflow: 'hidden',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
        }
    },
    searchBar: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        color: theme.white,
        borderRadius: theme.radius.xl,
        '&:focus': {
            borderColor: theme.colors.cyan[6],
        },
        input: {
            color: theme.white,
            '&::placeholder': {
                color: theme.colors.gray[5],
            }
        }
    },
    productImage: {
        width: '100%',
        height: 260,
        objectFit: 'cover',
        transition: 'transform 0.5s ease',
    },
    imageWrapper: {
        overflow: 'hidden',
        position: 'relative',
        '&:hover img': {
            transform: 'scale(1.05)',
        }
    },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)',
        opacity: 0.6,
        transition: 'opacity 0.3s',
    },
    addToCartBtn: {
        position: 'absolute',
        bottom: 20,
        right: 20,
        opacity: 0,
        transform: 'translateY(10px)',
        transition: 'all 0.3s ease',
        '.mantine-Paper-root:hover &': {
            opacity: 1,
            transform: 'translateY(0)',
        }
    }
}));

function Store() {
    const { classes, theme } = useStyles();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [modelData, setModelData] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLocalLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const loaderRef = useRef(null);

    const fetchProducts = async (pageNo) => {
        if (loading) return;
        setLocalLoading(true);
        try {
            const res = await fetch(`${url}/products?pageNo=${pageNo}&pageSize=12`);
            const data = await res.json();

            if (data.data && data.data.length > 0) {
                setModelData(prev => pageNo === 1 ? data.data : [...prev, ...data.data]);
                if (data.data.length < 12) setHasMore(false);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLocalLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts(1);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                setPage(prev => {
                    const newPage = prev + 1;
                    fetchProducts(newPage);
                    return newPage;
                });
            }
        }, { threshold: 1 });

        if (loaderRef.current) observer.observe(loaderRef.current);
        return () => observer.disconnect();
    }, [hasMore, loading]);

    const handleAddToCart = (e, item) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(addCartItem({ ...item, quantity: 1 }));
        toast.success(`Added ${item.title} to Cart`);
    };

    return (
        <div className={classes.wrapper}>
            <Container size="xl">
                {/* Header */}
                <Stack align="center" spacing="xs" mb={50}>
                    <Title order={1} style={{ color: theme.white, fontSize: 48, fontWeight: 900, letterSpacing: -1 }}>
                        The <Text span inherit variant="gradient" gradient={{ from: 'cyan', to: 'indigo' }}>Store</Text>
                    </Title>
                    <Text color="dimmed" size="lg" align="center" style={{ maxWidth: 600 }}>
                        Explore our curated collection of premium 3D models, prints, and gadgets.
                    </Text>

                    {/* Search Bar - Visual Only for now */}
                    <div style={{ position: 'relative', width: '100%', maxWidth: 500, marginTop: 20 }}>
                        <TextInput
                            placeholder="Search products..."
                            startIcon={<IconSearch size={18} />}
                            radius="xl"
                            size="md"
                            classNames={{ input: classes.searchBar }}
                            styles={{ input: { backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white' } }}
                        />
                    </div>
                </Stack>

                {/* Product Grid */}
                <Grid gutter="xl">
                    {modelData.map((item, index) => (
                        <Grid.Col key={`${item._id}-${index}`} xs={12} sm={6} md={4} lg={3}>
                            <Link to={`/product/${item._id}`} style={{ textDecoration: 'none' }}>
                                <Paper className={classes.glassPanel} p={0}>
                                    <div className={classes.imageWrapper}>
                                        <Image
                                            src={item.coverImage?.startsWith('http') ? item.coverImage : `https://backend.cdp3d.com${item.coverImage}`}
                                            alt={item.title}
                                            className={classes.productImage}
                                        />
                                        <div className={classes.overlay} />

                                        <ActionIcon
                                            className={classes.addToCartBtn}
                                            variant="gradient"
                                            gradient={{ from: 'cyan', to: 'indigo' }}
                                            size="xl"
                                            radius="xl"
                                            onClick={(e) => handleAddToCart(e, item)}
                                        >
                                            <IconShoppingCart size={20} />
                                        </ActionIcon>
                                    </div>

                                    <Stack p="md" spacing={4}>
                                        <Group position="apart" align="start">
                                            <Text color="white" weight={600} lineClamp={1} title={item.title}>
                                                {item.title}
                                            </Text>
                                        </Group>

                                        <Text color="dimmed" size="xs">
                                            {item.author || "CDP3D Designs"}
                                        </Text>

                                        <Group position="apart" mt="sm">
                                            <Text color="cyan" weight={700} size="lg">
                                                ₹{item.currentPrice}
                                            </Text>
                                            {item.currentPrice < item.previousPrice && (
                                                <Badge variant="filled" color="red" size="sm">
                                                    {Math.floor(((item.previousPrice - item.currentPrice) / item.previousPrice) * 100)}% OFF
                                                </Badge>
                                            )}
                                        </Group>
                                    </Stack>
                                </Paper>
                            </Link>
                        </Grid.Col>
                    ))}
                </Grid>

                {/* Loading / Infinite Scroll */}
                <div ref={loaderRef} style={{ marginTop: 40, textAlign: 'center', minHeight: 40 }}>
                    {loading && <Loader variant="dots" color="cyan" size="xl" />}
                    {!hasMore && modelData.length > 0 && (
                        <Text color="dimmed" size="sm" mt="xl">
                            You've reached the end of the universe.
                        </Text>
                    )}
                </div>
            </Container>
        </div>
    );
}

export default Store;
